import { AiSparkle, AiSurface } from '@/components/AiSurface';
import { featureFlags } from '@/featureFlags';
import {
  DOCK_ACCEPTED_FILES,
  type AiDockControls,
  type DockAttachment,
  type LiveTurn,
} from '@/features/ai-changeset/hooks/useAiChangeset';
import { useNavigationPosition } from '@/features/ai-changeset/hooks/useChangeNavigation';
import {
  historyItems,
  inputLocked,
  readingPage,
  readingRatio,
  sourceLabels,
  type DockTurn,
} from '@/features/ai-changeset/utils/dock-history';
import { dockPlacement, type DockPlacement } from '@/features/ai-changeset/utils/dock-placement';
import { VoiceInputButton } from '@/features/voice-input/components/VoiceInputButton';
import { useI18nStore, useT } from '@/i18n';
import { AiMenuSourceSchema, type AiMenuImportProgress } from '@restaurant/api-contracts';
import { Button, Link as F7Link, Preloader, Segmented } from 'framework7-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from 'react';

export type AiDockSuggestion = {
  id: string;
  label: string;
  text: string;
  mode: 'send' | 'fill';
};

interface DockProps {
  dock: AiDockControls;
  testId: string;
  title: string;
  placeholder: string;
  suggestions?: AiDockSuggestion[];
  onUndoCreated?: () => void;
  undoCreatedPending?: boolean;
  onRetryImport?: () => void;
  onReviewImport?: () => void;
  onImportUrl?: (url: string) => void;
}

function useDockHeight(ref: RefObject<HTMLDivElement | null>) {
  useLayoutEffect(() => {
    const el = ref.current;
    const page = el?.closest<HTMLElement>('.page');
    if (!el || !page) return;
    const measure = () => page.style.setProperty('--ai-dock-height', `${el.offsetHeight}px`);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => {
      observer.disconnect();
      page.style.removeProperty('--ai-dock-height');
    };
  }, [ref]);
}

function useDockFlip(
  ref: RefObject<HTMLDivElement | null>,
  placement: DockPlacement,
  ready: boolean,
) {
  const previous = useRef<DOMRect | null>(null);
  const running = useRef<Animation | null>(null);
  const wasReady = useRef(ready);
  useLayoutEffect(() => {
    const el = ref.current;
    const first = previous.current;
    if (!el || !first || !wasReady.current) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) return;
    running.current?.cancel();
    const last = el.getBoundingClientRect();
    const dx = first.left - last.left;
    const dy = first.top - last.top;
    if (Math.abs(dx) < 2 && Math.abs(dy) < 2) return;
    running.current = el.animate(
      [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'translate(0, 0)' }],
      { duration: 300, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
    );
  }, [ref, placement]);
  useLayoutEffect(() => {
    if (ref.current) previous.current = ref.current.getBoundingClientRect();
    wasReady.current = ready;
  });
}

function Typing({ testId }: { testId: string }) {
  const q = useT().aiChangeset;
  return (
    <span className="ai-typing" role="status" aria-label={q.proposing} data-testid={testId}>
      <span />
      <span />
      <span />
    </span>
  );
}

function FileChip({
  href,
  icon,
  label,
  testId,
}: {
  href: string;
  icon: string;
  label: string;
  testId: string;
}) {
  return (
    <a
      className="ai-dock__file"
      href={href || undefined}
      target="_blank"
      rel="noreferrer"
      data-testid={testId}
    >
      <i className="f7-icons" aria-hidden="true">
        {icon}
      </i>
      <span>{label}</span>
    </a>
  );
}

function AttachmentThumb({
  attachment,
  labels = [],
  testId,
}: {
  attachment: DockAttachment;
  labels?: string[];
  testId: string;
}) {
  if (attachment.kind === 'image' && attachment.url) {
    return (
      <a
        className="ai-dock__thumb"
        href={attachment.url}
        target="_blank"
        rel="noreferrer"
        title={labels[0] ?? attachment.fileName}
        data-testid={testId}
      >
        <img src={attachment.url} alt={labels[0] ?? attachment.fileName} />
      </a>
    );
  }
  if (attachment.kind === 'text') {
    return (
      <span className="ai-dock__file ai-dock__file--text" data-testid={testId}>
        <i className="f7-icons" aria-hidden="true">
          text_alignleft
        </i>
        <span>{labels[0] ?? attachment.fileName}</span>
      </span>
    );
  }
  if (labels.length > 1) {
    return (
      <>
        {labels.map((label, i) => (
          <FileChip
            key={label}
            href={attachment.url}
            icon="doc_text"
            label={label}
            testId={`${testId}-page-${i + 1}`}
          />
        ))}
      </>
    );
  }
  return (
    <FileChip
      href={attachment.url}
      icon="doc_text"
      label={labels[0] ?? attachment.fileName}
      testId={testId}
    />
  );
}

function UserBubble({
  text,
  attachments,
  labels,
  testId,
}: {
  text: string;
  attachments: DockAttachment[];
  labels?: Map<string, string[]>;
  testId: string;
}) {
  return (
    <div className="ai-dock__user" data-testid={`${testId}-user`}>
      {attachments.length > 0 && (
        <div className="ai-dock__attachments">
          {attachments.map((a) => (
            <AttachmentThumb
              key={a.id}
              attachment={a}
              labels={labels?.get(a.id)}
              testId={`${testId}-attachment-${a.id}`}
            />
          ))}
        </div>
      )}
      {text && <p>{text}</p>}
    </div>
  );
}

function TurnOutcome({
  turn,
  dock,
  testId,
  onReview,
  onUndoCreated,
  undoCreatedPending,
  onReviewImport,
}: {
  turn: DockTurn<unknown>;
  dock: AiDockControls;
  testId: string;
  onReview: () => void;
  onUndoCreated?: () => void;
  undoCreatedPending?: boolean;
  onReviewImport?: () => void;
}) {
  const t = useT();
  const d = t.aiDock;
  const [confirming, setConfirming] = useState(false);
  const n = turn.changes.length;
  const id = `${testId}-outcome`;
  switch (turn.outcome) {
    case 'created':
      return (
        <div className="ai-dock__outcome" data-testid={id}>
          {confirming && onUndoCreated ? (
            <>
              <span>{d.deleteMenuConfirm}</span>
              <F7Link
                className={undoCreatedPending ? 'disabled' : undefined}
                color="red"
                onClick={onUndoCreated}
                data-testid={`${id}-delete`}
              >
                {d.deleteMenu}
              </F7Link>
              <F7Link onClick={() => setConfirming(false)} data-testid={`${id}-cancel`}>
                {t.cancel}
              </F7Link>
            </>
          ) : (
            <>
              <span>{d.menuCreated}</span>
              {onReviewImport && (
                <F7Link onClick={onReviewImport} data-testid={`${id}-check`}>
                  {t.menuImportReview.open}
                </F7Link>
              )}
              {onUndoCreated && (
                <F7Link color="red" onClick={() => setConfirming(true)} data-testid={`${id}-undo`}>
                  {d.undoCreated}
                </F7Link>
              )}
            </>
          )}
        </div>
      );
    case 'applied':
      return (
        <div className="ai-dock__outcome" data-testid={id}>
          {turn.verification && turn.verification.missingCount > 0 ? (
            <span className="ai-dock__outcome-warn" data-testid={`${id}-missing`}>
              {d.changesPartlyApplied(
                turn.verification.appliedCount,
                turn.verification.missingCount,
              )}
            </span>
          ) : (
            <span>{d.changesApplied(turn.verification?.appliedCount ?? n)}</span>
          )}
          {turn.canUndo && (
            <F7Link
              className={dock.applying ? 'disabled' : undefined}
              onClick={() => dock.undoTurn(turn.id)}
              data-testid={`${id}-undo`}
            >
              {t.aiChangeset.undo}
            </F7Link>
          )}
        </div>
      );
    case 'pending':
      return (
        <div className="ai-dock__outcome" data-testid={id}>
          <F7Link onClick={onReview} data-testid={`${id}-review`}>
            {d.reviewChanges(n)}
          </F7Link>
          <F7Link
            className={dock.applying || dock.busy ? 'disabled' : undefined}
            onClick={() => dock.applyTurn(turn.id, n)}
            data-testid={`${id}-apply`}
          >
            {t.aiChangeset.apply}
          </F7Link>
        </div>
      );
    case 'applying':
      return (
        <div className="ai-dock__outcome ai-dock__outcome--muted" data-testid={id}>
          {d.applyingChanges(n)}
        </div>
      );
    case 'discarded':
      return (
        <div className="ai-dock__outcome ai-dock__outcome--muted" data-testid={id}>
          {d.discarded}
        </div>
      );
    case 'undone':
      return (
        <div className="ai-dock__outcome ai-dock__outcome--muted" data-testid={id}>
          {d.undone}
        </div>
      );
    default:
      return null;
  }
}

function History({
  dock,
  testId,
  onReview,
  onUndoCreated,
  undoCreatedPending,
  onReviewImport,
}: {
  dock: AiDockControls;
  testId: string;
  onReview: () => void;
  onUndoCreated?: () => void;
  undoCreatedPending?: boolean;
  onReviewImport?: () => void;
}) {
  const t = useT();
  const d = t.aiDock;
  const locale = useI18nStore((s) => s.locale);
  const scroller = useRef<HTMLDivElement | null>(null);
  const items = useMemo(() => {
    const format = new Intl.DateTimeFormat(locale, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
    return historyItems(dock.turns, (iso) => format.format(new Date(iso)));
  }, [dock.turns, locale]);
  const { liveTurn, menuImport } = dock;
  const labels = useMemo(() => sourceLabels(menuImport?.sources ?? []), [menuImport]);
  const lastKey = `${items.at(-1)?.key ?? ''}:${liveTurn?.reply.length ?? -1}`;

  useLayoutEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lastKey]);

  return (
    <div className="ai-dock__history" ref={scroller} data-testid={`${testId}-history`}>
      {dock.hasMore && (
        <div className="ai-dock__older">
          <F7Link
            className={dock.loadingOlder ? 'disabled' : undefined}
            onClick={dock.loadOlder}
            data-testid={`${testId}-load-older`}
          >
            {dock.loadingOlder ? <Preloader size={16} /> : d.loadOlder}
          </F7Link>
        </div>
      )}
      {items.length === 0 && !liveTurn && (
        <p className="ai-dock__empty" data-testid={`${testId}-history-empty`}>
          {d.emptyHistory}
        </p>
      )}
      {items.map((item) => {
        if (item.kind === 'day') {
          return (
            <div key={item.key} className="ai-dock__separator">
              {item.label}
            </div>
          );
        }
        if (item.kind === 'author') {
          return (
            <div key={item.key} className="ai-dock__author">
              {item.name ?? d.teammate}
            </div>
          );
        }
        const { turn } = item;
        const turnId = `${testId}-turn-${turn.id}`;
        return (
          <div key={item.key} className="ai-dock__turn" data-testid={turnId}>
            {(turn.text || turn.attachments.length > 0) && (
              <UserBubble
                text={turn.text}
                attachments={turn.attachments}
                labels={labels}
                testId={turnId}
              />
            )}
            {turn.reply && (
              <div className="ai-dock__reply">
                <AiSparkle size={16} />
                <p>{turn.reply}</p>
              </div>
            )}
            <TurnOutcome
              turn={turn}
              dock={dock}
              testId={turnId}
              onReview={onReview}
              onUndoCreated={onUndoCreated}
              undoCreatedPending={undoCreatedPending}
              onReviewImport={onReviewImport}
            />
          </div>
        );
      })}
      {liveTurn && <LiveTurnView turn={liveTurn} testId={`${testId}-live`} />}
    </div>
  );
}

function LiveTurnView({ turn, testId }: { turn: LiveTurn; testId: string }) {
  return (
    <div className="ai-dock__turn" data-testid={testId}>
      <UserBubble text={turn.text} attachments={turn.attachments} testId={testId} />
      <div className="ai-dock__reply" aria-live="polite">
        <AiSparkle size={16} />
        {turn.reply ? <p>{turn.reply}</p> : <Typing testId={`${testId}-typing`} />}
      </div>
    </div>
  );
}

function ImportProgress({ progress, testId }: { progress: AiMenuImportProgress; testId: string }) {
  const d = useT().aiDock;
  return (
    <div className="ai-dock__progress" role="status" data-testid={`${testId}-progress`}>
      <div className="ai-dock__progress-track">
        <span style={{ width: `${Math.max(readingRatio(progress), 0.04) * 100}%` }} />
      </div>
      <div className="ai-dock__progress-text">
        {progress.pagesTotal > 0
          ? d.readingPage(readingPage(progress), progress.pagesTotal)
          : d.readingStart}
      </div>
      <div className="ai-dock__progress-sub">{d.dishesSoFar(progress.dishesSoFar)}</div>
    </div>
  );
}

function ReviewRow({ dock, testId }: { dock: AiDockControls; testId: string }) {
  const t = useT();
  const q = t.aiChangeset;
  const d = t.aiDock;
  const nav = useNavigationPosition(dock.navigation);
  const { fullViewLabel } = dock;
  return (
    <div className="ai-dock__review" data-testid={`${testId}-review`}>
      {dock.staleLabels.length > 0 && (
        <div className="ai-dock__stale" data-testid={`${testId}-stale`}>
          {q.staleNotice(dock.staleLabels.join(', '))}
        </div>
      )}
      {nav.total > 0 && fullViewLabel !== null && (
        <Segmented strong round className="ai-dock__view" data-testid={`${testId}-view`}>
          <Button
            active={dock.changesOnly}
            onClick={() => dock.showChangesOnly(true)}
            data-testid={`${testId}-view-changes`}
          >
            {q.changesOnly}
          </Button>
          <Button
            active={!dock.changesOnly}
            onClick={() => dock.showChangesOnly(false)}
            data-testid={`${testId}-view-full`}
          >
            {fullViewLabel}
          </Button>
        </Segmented>
      )}
      <div className="ai-dock__review-row">
        {nav.total > 0 && (
          <>
            <button
              type="button"
              className="ai-dock__step"
              disabled={!nav.canPrev}
              onClick={() => dock.navigation.step(-1)}
              aria-label={q.previousChange}
              data-testid={`${testId}-prev`}
            >
              <i className="f7-icons" aria-hidden="true">
                chevron_up
              </i>
            </button>
            <button
              type="button"
              className="ai-dock__step"
              disabled={!nav.canNext}
              onClick={() => dock.navigation.step(1)}
              aria-label={q.nextChange}
              data-testid={`${testId}-next`}
            >
              <i className="f7-icons" aria-hidden="true">
                chevron_down
              </i>
            </button>
            <span className="ai-dock__position" data-testid={`${testId}-position`}>
              {d.changesPosition(nav.position === null ? '–' : String(nav.position), nav.total)}
            </span>
          </>
        )}
        <div className="ai-dock__actions">
          <Button
            round
            className="ai-dock__discard"
            disabled={dock.discarding || dock.busy}
            onClick={dock.discardDraft}
            data-testid={`${testId}-discard`}
          >
            {q.discard}
          </Button>
          {dock.changeCount > 0 && (
            <Button
              round
              fill
              className="ai-dock__apply"
              disabled={dock.applying || !dock.reviewReady}
              onClick={dock.applyDraft}
              data-testid={`${testId}-apply`}
            >
              {q.apply}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function Composer({
  dock,
  testId,
  placeholder,
  suggestions,
  expanded,
  text,
  setText,
  onImportUrl,
}: {
  dock: AiDockControls;
  testId: string;
  placeholder: string;
  suggestions?: AiDockSuggestion[];
  expanded: boolean;
  text: string;
  setText: (value: string) => void;
  onImportUrl?: (url: string) => void;
}) {
  const t = useT();
  const d = t.aiDock;
  const fileInput = useRef<HTMLInputElement | null>(null);
  const input = useRef<HTMLInputElement | null>(null);
  const pendingCaret = useRef<number | null>(null);
  const locked = inputLocked(dock.phase);
  const canSend =
    dock.canSend && (text.trim().length > 0 || dock.attachments.length > 0) && !locked;
  const visibleSuggestions = suggestions?.slice(0, 5) ?? [];
  const hasSuggestions = visibleSuggestions.length > 0;
  const hasPendingDraftReview =
    dock.hasDraft || dock.phase === 'review' || dock.staleLabels.length > 0;
  const showSuggestions =
    expanded &&
    hasSuggestions &&
    text.length === 0 &&
    dock.attachments.length === 0 &&
    dock.uploading === 0 &&
    !dock.busy &&
    !locked &&
    dock.phase !== 'reading' &&
    !hasPendingDraftReview;

  useLayoutEffect(() => {
    if (pendingCaret.current === null) return;
    input.current?.focus();
    input.current?.setSelectionRange(pendingCaret.current, pendingCaret.current);
    pendingCaret.current = null;
  }, [text]);

  const send = (value = text) => {
    const trimmed = value.trim();
    const canSubmit =
      dock.canSend && (trimmed.length > 0 || dock.attachments.length > 0) && !locked;
    if (
      onImportUrl &&
      !locked &&
      dock.attachments.length === 0 &&
      AiMenuSourceSchema.safeParse({ kind: 'url', url: trimmed }).success
    ) {
      onImportUrl(trimmed);
      setText('');
      return;
    }
    if (!canSubmit) return;
    if (dock.submit(value)) setText('');
  };

  const fill = (value: string) => {
    const blank = value.indexOf('__');
    const next = blank === -1 ? value : `${value.slice(0, blank)}${value.slice(blank + 2)}`;
    pendingCaret.current = blank === -1 ? next.length : blank;
    input.current?.focus();
    setText(next);
  };

  return (
    <>
      {(dock.attachments.length > 0 || dock.uploading > 0) && (
        <div className="ai-dock__pending">
          {dock.attachments.map((a) => (
            <span key={a.id} className="ai-dock__pending-item">
              <AttachmentThumb attachment={a} testId={`${testId}-pending-${a.id}`} />
              <button
                type="button"
                className="ai-dock__pending-remove"
                onClick={() => dock.removeAttachment(a.id)}
                aria-label={d.removeAttachment}
                data-testid={`${testId}-pending-remove-${a.id}`}
              >
                <i className="f7-icons" aria-hidden="true">
                  xmark_circle_fill
                </i>
              </button>
            </span>
          ))}
          {dock.uploading > 0 && (
            <span className="ai-dock__pending-item ai-dock__uploading">
              <Preloader size={16} />
              {d.uploading}
            </span>
          )}
        </div>
      )}
      {showSuggestions && (
        <div className="ai-dock__suggestions" data-testid={`${testId}-suggestions`}>
          {visibleSuggestions.map((suggestion) => (
            <button
              key={suggestion.id}
              type="button"
              className="ai-dock__suggestion"
              onClick={() =>
                suggestion.mode === 'send' ? send(suggestion.text) : fill(suggestion.text)
              }
              data-testid={`${testId}-suggestion-${suggestion.id}`}
            >
              {suggestion.label}
            </button>
          ))}
        </div>
      )}
      <form
        className="ai-dock__composer"
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
      >
        <input
          ref={fileInput}
          type="file"
          accept={DOCK_ACCEPTED_FILES.join(',')}
          multiple
          hidden
          data-testid={`${testId}-file-input`}
          onChange={(e) => {
            void dock.addFiles(Array.from(e.target.files ?? []));
            e.target.value = '';
          }}
        />
        <button
          type="button"
          className="ai-dock__icon"
          disabled={locked}
          onClick={() => fileInput.current?.click()}
          aria-label={d.attach}
          title={d.attach}
          data-testid={`${testId}-attach`}
        >
          <i className="f7-icons" aria-hidden="true">
            camera
          </i>
        </button>
        <input
          ref={input}
          className="ai-dock__input"
          type="text"
          enterKeyHint="send"
          value={text}
          placeholder={placeholder}
          disabled={locked}
          onChange={(e) => setText(e.target.value)}
          onPaste={(e) => {
            const files = Array.from(e.clipboardData.files);
            if (files.length > 0) {
              e.preventDefault();
              void dock.addFiles(files);
            }
          }}
          aria-label={t.aiChangeset.propose}
          data-testid={`${testId}-input`}
        />
        {featureFlags.voiceInput && (
          <VoiceInputButton
            value={text}
            onChange={setText}
            disabled={locked}
            data-testid={`${testId}-voice`}
          />
        )}
        <button
          type="submit"
          className="ai-dock__send"
          disabled={!canSend}
          aria-label={d.send}
          data-testid={`${testId}-send`}
        >
          <i className="f7-icons" aria-hidden="true">
            arrow_up
          </i>
        </button>
      </form>
    </>
  );
}

export function AiDock({
  dock,
  testId,
  title,
  placeholder,
  suggestions,
  onUndoCreated,
  undoCreatedPending,
  onRetryImport,
  onReviewImport,
  onImportUrl,
}: DockProps) {
  const t = useT();
  const d = t.aiDock;
  const [expanded, setExpanded] = useState(false);
  const [text, setText] = useState('');
  const dockRef = useRef<HTMLDivElement | null>(null);
  const { phase, menuImport } = dock;

  const placement = dockPlacement({
    phase,
    applying: dock.applying,
    discarding: dock.discarding,
    notice: !!dock.error || dock.staleLabels.length > 0 || menuImport?.status === 'failed',
    expanded,
  });

  useDockHeight(dockRef);
  useDockFlip(dockRef, placement, !dock.loading);

  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExpanded(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [expanded]);

  const review = () => {
    setExpanded(false);
    dock.navigation.step(1);
  };

  const line =
    phase === 'review' && dock.summary && !dock.busy ? (
      <span className="quick-edit-draft-summary" data-testid={`${testId}-draft-summary`}>
        {dock.summary}
      </span>
    ) : dock.busy && !dock.reply ? (
      <Typing testId={`${testId}-typing`} />
    ) : (
      <span className="ai-dock__line-text" data-testid={`${testId}-reply`}>
        {dock.reply || title}
      </span>
    );

  return (
    <AiSurface
      ref={dockRef}
      busy={dock.busy || dock.applying || phase === 'reading'}
      className={[
        'ai-dock',
        placement === 'bottom' && 'ai-dock--floating',
        expanded && 'ai-dock--expanded',
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid={testId}
    >
      {expanded ? (
        <>
          <div className="ai-dock__header">
            <AiSparkle size={18} />
            <div className="ai-dock__heading">
              <strong>{title}</strong>
              <span>{d.sharedWithTeam}</span>
            </div>
            <button
              type="button"
              className="ai-dock__toggle"
              onClick={() => setExpanded(false)}
              aria-label={d.collapse}
              aria-expanded
              data-testid={`${testId}-collapse`}
            >
              <i className="f7-icons" aria-hidden="true">
                chevron_down
              </i>
            </button>
          </div>
          <History
            dock={dock}
            testId={testId}
            onReview={review}
            onUndoCreated={onUndoCreated}
            undoCreatedPending={undoCreatedPending}
            onReviewImport={onReviewImport}
          />
        </>
      ) : (
        <button
          type="button"
          className="ai-dock__line"
          onClick={() => setExpanded(true)}
          aria-label={d.expand}
          aria-expanded={false}
          data-testid={`${testId}-expand`}
        >
          <AiSparkle size={16} />
          {line}
          <i className="f7-icons ai-dock__chevron" aria-hidden="true">
            chevron_up
          </i>
        </button>
      )}
      {phase === 'reading' && menuImport && (
        <ImportProgress progress={menuImport} testId={testId} />
      )}
      {menuImport?.status === 'failed' && (
        <div className="ai-dock__notice ai-dock__notice--error" data-testid={`${testId}-failed`}>
          <span>
            {menuImport.failureReason
              ? `${d.importFailed} ${menuImport.failureReason}`
              : d.importFailed}
          </span>
          {onRetryImport && (
            <F7Link onClick={onRetryImport} data-testid={`${testId}-retry`}>
              {d.tryAgain}
            </F7Link>
          )}
        </div>
      )}
      {menuImport?.status === 'ready' && menuImport.sources.some((s) => s.failed) && (
        <p className="ai-dock__notice" data-testid={`${testId}-pages-not-read`}>
          {d.pagesNotRead(
            menuImport.sources
              .filter((s) => s.failed)
              .map((s) => s.label)
              .join(', '),
          )}
        </p>
      )}
      {phase === 'locked' && (
        <p className="ai-dock__notice" data-testid={`${testId}-locked`}>
          {d.lockedByOther}
        </p>
      )}
      {(phase === 'review' || dock.staleLabels.length > 0) && (
        <ReviewRow dock={dock} testId={testId} />
      )}
      {dock.error && phase !== 'locked' && (
        <p className="ai-dock__notice ai-dock__notice--error" data-testid={`${testId}-error`}>
          {dock.error}
        </p>
      )}
      <Composer
        dock={dock}
        testId={testId}
        placeholder={placeholder}
        suggestions={suggestions}
        expanded={expanded}
        text={text}
        setText={setText}
        onImportUrl={onImportUrl}
      />
    </AiSurface>
  );
}
